import {useEffect, useRef} from 'react';
import {useDispatch} from "react-redux";
import {cn} from '@/utils';
import UploadIcon from '@/Icons/icon-dpo-upload.svg';
import {
  getSingleValueShape,
  isSingleValueShape,
  getCustomSizeType,
} from '../../../shapeUtils';
import {useImmer} from "use-immer";
import {PrimaryButtonComponent} from '@/components';
import { useSelector } from 'react-redux';
import {
  fetchChangeOptions,
  storeAddToCartFormData, storeDpoData, matrixSelector
} from '../../../slices/matrixSlice';

const Upload = ({
                  startUploadJourney,
                  dpoSettings,
                  dpoQuote,
                  triggerStartUploadJourney,
                  shapes,
                  baseUrl,
                  code,
                  product,
                  productId,
                  defaultConfig,
                  webproduct,
                  customQuantities,
                  currentOptions,
                  optionsOverwrite,
                  qty,
                  isMobile,
                  productTags,
                  showTitleBar,
                  is_custom_shape,
                  closeModal
                }) => {
  const { AveryCustom } = useSelector(matrixSelector);

  const [componentState, updateComponentState] = useImmer({
    showPreview: false,
    flowNotSupported: false,
    previewPath: "",
  });
  const urlParams = new URLSearchParams(window.location.search);
  const cartForm = useRef();
  const dispatch = useDispatch();
  const dpoShapeMapping = {
      custom: { shape: 'sha_custom', orgShape: 'sha_custom', checkCorners: false, },
      rect: { shape: 'sha_rectangle_round', orgShape: 'sha_rectangle_round', checkCorners: true, },
      ellipse: { shape: 'sha_oval', orgShape: 'sha_oval', checkCorners: false, },
  };

  const getIFrameSrc = () => {
    const {
      sku,
      profile,
      material: materialId,
      labelHeight: height,
      labelWidth: width,
      cornerRadius,
      labelShape: shape,
      kissCutSpotColor,
      addWhiteColor = '',
    } = dpoQuote;

    let {qau_widget_endpoint: src} = dpoSettings;

    let queryParams = {
      mode: isMobile ? 'mobile' : 'desktop',
      sku,
      materialId,
      addWhiteColor: addWhiteColor === 'Y',
    };

    if (profile === 'sticker') {
      queryParams = {...queryParams, shape, width, height, cornerRadius};
    }

    if (is_custom_shape) {
      queryParams['shape'] = 'custom';
      queryParams['height'] = 0;
      queryParams['width'] = 0;
      delete queryParams['cornerRadius'];
      // queryParams['cornerRadius'] = 0;
    }

    if (kissCutSpotColor) {
      queryParams['kissCutSpotColor'] = escape(kissCutSpotColor);
    }

    const book_binding = currentOptions.find((opt) => opt.id === 'book_binding');
    if (book_binding) {
      const {value} = book_binding;
      if (value.includes('foil') || value.includes('spot')) {
        src = src.replace(/consumer=[^&]*/, 'consumer=finishEU');
        const finishes = [];
        if (value.includes('foil')) {
          finishes.push('Folie');
        }
        if (value.includes('spot')) {
          finishes.push('Spotlack');
        }

        queryParams['finish'] = finishes;
      }
    }

    return (
      src +
      '&' +
      Object.keys(queryParams)
        .map((key) => {
          const value = queryParams[key];
          if (Array.isArray(value)) {
            return value.map(v => `${key}=${v}`).join('&');
          }
          return `${key}=${value}`;
        })
        .join('&')
    );
  };

  function dpoFinish(e) {
    const {data} = e,
      {sku} = data;

    if (data && sku) {
      let autoSubmitForm = !is_custom_shape;
      const {
        panel_width: labelWidth,
        panel_height: labelHeight,
        check_design: checkDesign,
        artwork_file_type,
        artwork_pdf_url,
        artwork_url,
        preview_urls: {medium: qauPreview, xlarge},
        artwork_id: artworkId,
        artwork_status,
        shape: dpoShape,
      } = data;

      const {
        labelWidth: quoteWidth,
        labelHeight: quoteHeight,
      } = dpoQuote;

      const { shape } = optionsOverwrite;

      let overwriteFormat = false;
      let overwriteShape = false;
      let flowNotSupported = false;
      let setCustomSize = false;
      let qauPdf = artwork_pdf_url;

      if (artwork_file_type === 'pdf' && qauPdf === null && artwork_status === 'WITH_DIELINE' && artwork_url) {
        overwriteShape = 'sha_custom';
        autoSubmitForm = false;
        flowNotSupported = is_custom_shape === true;

        updateComponentState((draft) => {
          draft.showPreview = true;
          draft.flowNotSupported = is_custom_shape === true;
          draft.previewPath = xlarge;
        });

        qauPdf = artwork_url;
      } else if (qauPdf === null && artwork_status === 'WITH_FINISHES' && artwork_url) {
        // Premium finishes QAU exit
        autoSubmitForm = true;
        qauPdf = artwork_url;
        overwriteShape = 'sha_rectangle_round';
        overwriteFormat = `custom_${Math.ceil(labelWidth)}_${Math.ceil(
          labelHeight
        )}_${getCustomSizeType(overwriteShape)}`;
      }

      let {selection_id} = shapes;

      if (!overwriteShape && !shape.toLowerCase().includes('_tag') && !shape.toLowerCase().includes('_wpau_') && !shape.toLowerCase().includes('rings')) {
         const { [dpoShape]:shapeAttributes } = dpoShapeMapping;

        if (shapeAttributes) {
          if (shape !== shapeAttributes.shape) {
           overwriteShape = shapeAttributes.shape;
           if (shapeAttributes.checkCorners && !shape.includes('_round')) {
             overwriteShape = overwriteShape.replace('_round', '');
           }
           selection_id = overwriteShape;
           setCustomSize = true;
         }
        }
      }

      if (is_custom_shape) {
        if (labelWidth === labelHeight) {
          // Square is not working for now.
          // selection_id = 'sha_square_round';
          selection_id = 'sha_rectangle_round';
        } else {
          selection_id = 'sha_rectangle_round';
        }
      }

      if (sku.includes('Custom_Shape') || sku.includes('Custom_Oversize_Shape')) {
        overwriteFormat = `custom_${Math.ceil(labelWidth)}_${Math.ceil(
          labelHeight
        )}_${getCustomSizeType(selection_id)}`;
      } else if (((labelWidth !== quoteWidth || labelHeight !== quoteHeight) || setCustomSize)) {
        if (sku.includes('Custom_Size')) {
          overwriteFormat = `custom_${Math.ceil(labelWidth)}_${Math.ceil(
            labelHeight
          )}_${getCustomSizeType(selection_id)}`;
        }

        if (labelWidth === labelHeight &&
          !isSingleValueShape(selection_id)) {
          const newShape = getSingleValueShape(selection_id);
          if (newShape) {
            overwriteShape = newShape;
            if (sku.includes('Custom_Size')) {
              overwriteFormat = `custom_${Math.ceil(labelWidth)}_${Math.ceil(
                labelHeight
              )}_${getCustomSizeType(newShape)}`;
            }
          }
        }
      }

      const formElements = {
        sku,
        checkDesign,
        labelHeight,
        labelWidth,
        qauPdf,
        qauPreview,
        artworkId,
        product_tags: productTags,
        product,
        webproduct,
        qty,
        current_selection_id: `prod_${product}_${new Date().getTime()}_${Math.floor(
          Math.random() * 100
        )}`,
        [`print_options[PP][]`]: code,
      };
      const newOptions = {};
      const dpoFormElements = {...formElements};
      for (let currentOption of currentOptions) {
        const {id, is_product_part} = currentOption;
        let {value} = currentOption;

        if (id === 'shape' && overwriteShape) {
          value = overwriteShape;
          newOptions[id] = value;
        }

        if (id === 'format' && overwriteFormat) {
          value = overwriteFormat;
          newOptions[id] = value;
        }

        if (is_product_part) {
          formElements[`print_options[PP_${code}][${id}][]`] = value;
        } else {
          formElements[`print_options[${id}][${id}][]`] = value;
        }
      }

      Object.keys(formElements).map((key) => {
        const newElement = document.createElement('input');
        newElement.id = key;
        newElement.name = key;
        newElement.type = 'hidden';
        newElement.value = formElements[key];
        cartForm.current.appendChild(newElement);
      });

      if (autoSubmitForm) {
        cartForm.current.requestSubmit();
      } else if (is_custom_shape && !flowNotSupported) {
        delete data.file;
        delete dpoFormElements.qty;
        dispatch(storeDpoData(data));
        dispatch(storeAddToCartFormData(dpoFormElements));
        dispatch(
          fetchChangeOptions({
            variables: {
              productId,
              defaultConfig,
              optionsOverwrite: {...optionsOverwrite, ...newOptions},
              customQuantities,
            },
          })
        );
      }
    }
  }

  const registerEventHandler = () => {
    window.addEventListener('message', dpoFinish);
  };

  useEffect(() => {
    return () => {
      window.removeEventListener('message', dpoFinish);
    };
  }, []);

  useEffect(
    () => {
      if (componentState.showPreview) {
        showTitleBar(true, Drupal.t('You uploaded a PDF that includes a supported die line!'));
      }
    },
    [componentState.showPreview]
  );

  /**
   * This is triggered by onSubmit of the form. It may just be a noop in case
   * we are not in AveryCustom context. If we are in AveryCustom context this
   * will emmit a custom event and prevent the regular form submission.
   */
  const preSubmitHandler = (event) => {
    if (! AveryCustom.client) {
      return;
    }
    event.preventDefault();
    var data = new FormData(cartForm.current);
    const uploadDone = new CustomEvent("avery-custom-dpo-qau-done", { detail: data });
    window.dispatchEvent(uploadDone);
    closeModal();
    return;
  }

  return (
    <Choose>
      <When condition={startUploadJourney}>
        <If condition={!componentState.showPreview}>
          <iframe
            ref={registerEventHandler}
            src={getIFrameSrc()}
            className={cn('h-full w-full', !isMobile ? 'tablet:h-[700px]' : '')}
          />
        </If>
        <form
          ref={cartForm}
          className="bg-neutral-light"
          action={urlParams.get('use_dpo_proxy') ? '/dpo_proxy.php' : baseUrl + '/dpo/product/directAddToCart'}
          onSubmit={(e) => preSubmitHandler(e)}
          method="POST"
        >
          <If condition={componentState.showPreview}>
            <Choose>
              <When condition={componentState.flowNotSupported}>
                <div className="p-xl bg-white">
                  <div className="flex justify-center">
                    {Drupal.t('The custom shape journey currently does not support designs with an existing die-line. Please use standard shapes with the correct dimensions.')}
                  </div>

                </div>
                <div className="flex justify-end px-2xl py-xl bg-neutral-light">
                  <PrimaryButtonComponent
                    onClick={() => closeModal()}
                    className="font-bold"
                    large
                    red
                  >
                    {Drupal.t('OK')}</PrimaryButtonComponent>
                </div>
              </When>
              <Otherwise>
                <div className="p-xl bg-white">
                  <div className="flex justify-center">
                    <img alt="" src={componentState.previewPath}/>
                  </div>
                </div>
                <div className="flex justify-end px-2xl py-xl bg-neutral-light">
                  <PrimaryButtonComponent
                    onClick={() => cartForm.current.requestSubmit()}
                    className="font-bold"
                    large
                    green
                  >
                    {Drupal.t('Add to basket')}</PrimaryButtonComponent>
                </div>
              </Otherwise>
            </Choose>
          </If>
        </form>
      </When>
      <Otherwise>
        <div id='upload' className="flex flex-col flex-wrap bg-neutral-light border-2 border-neutral-dark cursor-pointer font-semibold text-center text-xs tablet:text-sm shadow-[shadow:var(--theme-card-shadow)] hover:ring-1 hover:ring-[color:var(--theme-link-border-color-4)] hover:border-[color:var(--theme-link-border-color-4)]">
          <button
            onClick={triggerStartUploadJourney}
            className="flex flex-col w-full h-full"
          >
            <div className="flex flex-col gap-xs items-center justify-center w-full bg-white p-lg laptop:flex-row">
              <div className="mx-3xs h-[4rem] w-[4rem]">
                <UploadIcon />
              </div>
              <div className="laptop:text-left">
                <div className="text-2xl font-semibold">
                  {Drupal.t('Upload')}
                </div>
                <div className="text-xs font-semibold">
                  {Drupal.t('your artwork')}
                </div>
              </div>
            </div>
            <ul
              className="list-disc text-left pl-2xl pr-xl my-sm"
              dangerouslySetInnerHTML={{
                __html: Drupal.t(
                  '<li>Upload your finished artwork</li><li><strong>Supported Files Types</strong>: .jpg, .png, .psd, .eps, .pdf, .ai, .tiff</li>'
                ),
              }}
            ></ul>
          </button>
        </div>
      </Otherwise>
    </Choose>
  );
};

export default Upload;
